import type { ModalProps, PopconfirmProps } from 'antd';
import { Col, Row } from 'antd';
import type { TooltipProps } from 'antd/lib';
import React from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';

import useSteps from 'hooks/useSteps';
import type { MayBe } from 'modules/global/typing/common/common';

import Button from 'ui/base/Button';
import Modal from 'ui/base/Modal';
import Popconfirm from 'ui/base/Popconfirm';
import Steps from 'ui/base/Steps';
import Tooltip from 'ui/base/Tooltip';

export type ModalStep = {
  key: string;
  content: React.ReactNode;
  title: React.ReactNode;
  nextStep?: string;
  prevStep?: string;
  isLastStep?: boolean | (() => boolean);
  disabled?: boolean;
};

type FooterProps = {
  className?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  hideSubmit?: boolean;
  onSubmit?: () => void;
  onCancel?: () => void;
  handleNext?: MayBe<() => void>;
  handlePrev?: MayBe<() => void>;
  cancelTooltipProps?: PopconfirmProps;
  prevTooltipTranslation?: string;
  nextTooltipTranslation?: string;
  nextTranslation?: MessageDescriptor;
  submitTranslation?: MessageDescriptor;
  cancelTranslation?: MessageDescriptor;
  previousTranslation?: MessageDescriptor;
};

export const Footer: React.FC<FooterProps> = ({
  isLoading,
  isDisabled,
  onSubmit,
  onCancel,
  handleNext,
  handlePrev,
  hideSubmit,
  className,
  cancelTooltipProps,
  prevTooltipTranslation,
  nextTooltipTranslation,
  nextTranslation = { id: 'next', defaultMessage: 'Next' },
  cancelTranslation = { id: 'cancel', defaultMessage: 'Cancel' },
  previousTranslation = { id: 'previous', defaultMessage: 'Previous' },
  submitTranslation = { id: 'page.keyDeposit.appointments.createModal.submit', defaultMessage: 'Create' }
}) => {
  const hasPrevTooltipTranslation = Boolean(prevTooltipTranslation);
  const hasNextTooltipTranslation = Boolean(nextTooltipTranslation);

  const [showPrevTooltip, setPrevTooltipVisible] = React.useState(false);
  const [showNextTooltip, setNextTooltipVisible] = React.useState(false);

  const disabled = isLoading || isDisabled;

  const showPrev = typeof handlePrev === 'function';
  const showNext = typeof handleNext === 'function';

  const handlePrevTooltipChange = React.useCallback<NonNullable<TooltipProps['onOpenChange']>>(
    (isVisible) => setPrevTooltipVisible(hasPrevTooltipTranslation && isVisible),
    [hasPrevTooltipTranslation]
  );

  const handleNextTooltipChange = React.useCallback<NonNullable<TooltipProps['onOpenChange']>>(
    (isVisible) => setNextTooltipVisible(hasNextTooltipTranslation && isVisible),
    [hasNextTooltipTranslation]
  );

  return (
    <Row justify="space-between" className={className}>
      {cancelTooltipProps && (
        <Popconfirm {...cancelTooltipProps}>
          <Button disabled={isLoading} data-cy="cancel-btn">
            <FormattedMessage {...cancelTranslation} />
          </Button>
        </Popconfirm>
      )}
      {!cancelTooltipProps && (
        <Button onClick={onCancel} disabled={isLoading} data-cy="cancel-btn">
          <FormattedMessage {...cancelTranslation} />
        </Button>
      )}
      <Row justify="center" gutter={8}>
        {showPrev && (
          <Tooltip
            translate
            placement="topRight"
            open={showPrevTooltip}
            title={prevTooltipTranslation}
            onOpenChange={handlePrevTooltipChange}
          >
            {/* `Col` prevents the Tooltip to take `disabled` state into account */}
            <Col>
              <Button onClick={handlePrev} disabled={isLoading} data-cy="prev-btn">
                <FormattedMessage {...previousTranslation} />
              </Button>
            </Col>
          </Tooltip>
        )}
        {showNext && (
          <Tooltip
            translate
            placement="topRight"
            open={showNextTooltip}
            title={nextTooltipTranslation}
            onOpenChange={handleNextTooltipChange}
          >
            {/* `Col` prevents the Tooltip to take `disabled` state into account */}
            <Col>
              <Button type="primary" onClick={handleNext} disabled={disabled} data-cy="next-btn">
                <FormattedMessage {...nextTranslation} />
              </Button>
            </Col>
          </Tooltip>
        )}
        {!hideSubmit && (
          <Col>
            <Button
              type="primary"
              onClick={onSubmit}
              disabled={disabled}
              loading={isLoading}
              data-cy="step-modal-submit-btn"
            >
              <FormattedMessage {...submitTranslation} />
            </Button>
          </Col>
        )}
      </Row>
    </Row>
  );
};

type StepModalProps = {
  title?: React.ReactNode;
  steps?: ModalStep[];
  hasError?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  large?: boolean;
  onSubmit?: () => void;
  onCancel?: () => void;
  width?: ModalProps['width'];
  analytics?: { name: string };
  className?: string;
  cancelTooltipProps?: PopconfirmProps;
  footer?: React.ComponentType<FooterProps>;
  footerProps?: Partial<FooterProps>;
  mask?: ModalProps['mask'];
  'data-cy'?: string;
};

const StepModal: React.FC<StepModalProps> = ({
  title,
  steps: stepsProp = [],
  hasError,
  disabled,
  isLoading,
  onSubmit,
  onCancel,
  analytics,
  className,
  large,
  mask,
  width,
  footer: FooterComponent = Footer,
  cancelTooltipProps,
  footerProps = {},
  'data-cy': dataCy
}) => {
  // @ts-expect-error useSteps is JS — second arg (defaultStep) is optional but not marked as such in JSDoc
  const { steps, currentStep, currentStepIndex, handleNext, handlePrev, isLastStep } = useSteps(stepsProp) as {
    steps: ModalStep[];
    currentStep: MayBe<ModalStep>;
    currentStepIndex: number;
    handleNext: () => void;
    handlePrev: () => void;
    isLastStep: boolean;
  };

  return (
    <Modal
      open
      destroyOnHidden
      error={hasError}
      title={title}
      analytics={analytics}
      className={className}
      confirmLoading={isLoading}
      large={large}
      mask={mask}
      data-cy={dataCy}
      width={width}
      footer={
        <FooterComponent
          onSubmit={onSubmit}
          onCancel={onCancel}
          isLoading={isLoading}
          isDisabled={disabled || currentStep?.disabled}
          hideSubmit={!isLastStep}
          handleNext={currentStep?.nextStep ? handleNext : undefined}
          handlePrev={currentStep?.prevStep ? handlePrev : undefined}
          cancelTooltipProps={cancelTooltipProps}
          {...footerProps}
        />
      }
    >
      {steps.length > 1 && (
        <Steps
          className="p-0 mb-4"
          current={currentStepIndex}
          items={steps.map(({ key: _key, content: _content, nextStep: _nextStep, prevStep: _prevStep, isLastStep: _, title: stepTitle, ...rest }) => ({
            ...rest,
            title: title && stepTitle,
          }))}
        />
      )}
      {currentStep?.content}
    </Modal>
  );
};

export default React.memo(StepModal);
